import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui';
import { fileApiService } from '../../services/api/fileApiService';
import { ApiError } from '../../services/api/apiClient';
import type { BecasDocuments } from '../../services/api/becasApiService';

interface Props {
  initial?: BecasDocuments | null;
  onSaved: (fileId: string) => Promise<void>;
}

export function BecasDocumentsForm({ initial, onSaved }: Props) {
  const { t } = useTranslation();
  const [fileName, setFileName] = useState(initial?.transcript_file_name || '');
  const [fileId, setFileId] = useState(initial?.transcript_file_id || '');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const uploaded = await fileApiService.upload(file);
      setFileId(uploaded.id);
      setFileName(uploaded.file_name || file.name);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('becas.errors.upload'));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!fileId) {
      setError(t('becas.documents.required'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSaved(fileId);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('becas.errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <p className="text-dark-400 text-sm leading-relaxed">{t('becas.documents.intro')}</p>
      <label className="block rounded-2xl border border-dashed border-brand-lightBlue/40 bg-brand-darkNavy/50 p-6 text-center cursor-pointer hover:border-brand-yellow/50 transition-colors">
        <FileText className="w-8 h-8 text-brand-yellow mx-auto mb-3" />
        <span className="block text-white font-semibold mb-1">{t('becas.documents.transcript')}</span>
        <span className="block text-dark-400 text-sm mb-4">{t('becas.documents.hint')}</span>
        <input
          type="file"
          accept="application/pdf,image/*"
          className="mx-auto block text-sm text-dark-300 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-brand-yellow file:text-brand-darkNavy file:font-semibold"
          onChange={(e) => void handleFile(e.target.files?.[0])}
        />
      </label>
      {(fileName || fileId) && (
        <p className="flex items-center gap-2 text-sm text-green-400">
          <CheckCircle2 className="w-4 h-4" />
          {fileName || t('becas.documents.uploaded')}
        </p>
      )}
      {uploading && <p className="text-sm text-brand-yellow">{t('becas.documents.uploading')}</p>}
      {error && (
        <p className="text-sm text-accent-rose bg-accent-rose/10 border border-accent-rose/30 rounded-xl px-4 py-3">
          {error}
        </p>
      )}
      <Button type="submit" variant="primary" fullWidth isLoading={saving || uploading} disabled={!fileId}>
        {t('becas.documents.continue')}
      </Button>
    </form>
  );
}
